import React, { useRef, useState } from "react";
import { Buildings } from "@phosphor-icons/react";
import { formatApiError, updateCompany, uploadFile } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import SettingsSection from "./SettingsSection";

const LOGO_MAX_BYTES = 2 * 1024 * 1024;          // 2 MB
const LOGO_MIN_W = 80;
const LOGO_MIN_H = 40;
const LOGO_TYPES = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];

// Returns null when the file is an acceptable logo, else a human-readable error string.
async function validateLogo(file) {
  if (!LOGO_TYPES.includes(file.type)) return "Please choose a PNG, JPG, WEBP, or SVG image.";
  if (file.size > LOGO_MAX_BYTES) return "Logo must be under 2 MB.";
  if (file.type === "image/svg+xml") return null; // vector scales — skip pixel-size check
  const dims = await new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => { URL.revokeObjectURL(url); resolve({ w: img.naturalWidth, h: img.naturalHeight }); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
  if (!dims) return "That image couldn't be read. Try another file.";
  if (dims.w < LOGO_MIN_W || dims.h < LOGO_MIN_H)
    return `Logo is too small (${dims.w}×${dims.h}). Use at least ${LOGO_MIN_W}×${LOGO_MIN_H}px (recommended ~240×80).`;
  return null;
}

export default function BrandingPanel({ user, setUser }) {
  const logoInputRef = useRef(null);
  const iconInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [iconUploading, setIconUploading] = useState(false);
  const [variantSaving, setVariantSaving] = useState(false);
  const [companyName, setCompanyName] = useState(user.company?.name || "");
  const [nameSaving, setNameSaving] = useState(false);

  const onLogoSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";            // allow re-selecting the same file later
    if (!file) return;
    const validationErr = await validateLogo(file);
    if (validationErr) { toast.error(validationErr); return; }
    setUploading(true);
    try {
      const up = await uploadFile(file);
      const company = await updateCompany({ logo_url: up.url });
      setUser((u) => ({ ...u, company: { ...(u.company || {}), logo_url: company.logo_url } }));
      toast.success("Logo updated");
    } catch (err) {
      const msg = err.response?.status === 503
        ? "File storage isn't configured yet. Contact your administrator."
        : formatApiError(err.response?.data?.detail);
      toast.error(msg);
    } finally { setUploading(false); }
  };

  const removeLogo = async () => {
    setUploading(true);
    try {
      const company = await updateCompany({ logo_url: null });
      setUser((u) => ({ ...u, company: { ...(u.company || {}), logo_url: company.logo_url } }));
      toast.success("Logo removed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const onIconSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const validationErr = await validateLogo(file);
    if (validationErr) { toast.error(validationErr); return; }
    setIconUploading(true);
    try {
      const up = await uploadFile(file);
      const company = await updateCompany({ icon_url: up.url });
      setUser((u) => ({ ...u, company: { ...(u.company || {}), icon_url: company.icon_url } }));
      toast.success("Icon updated");
    } catch (err) {
      const msg = err.response?.status === 503
        ? "File storage isn't configured yet. Contact your administrator."
        : formatApiError(err.response?.data?.detail);
      toast.error(msg);
    } finally { setIconUploading(false); }
  };

  const removeIcon = async () => {
    setIconUploading(true);
    try {
      const company = await updateCompany({ icon_url: null });
      setUser((u) => ({ ...u, company: { ...(u.company || {}), icon_url: company.icon_url } }));
      toast.success("Icon removed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setIconUploading(false); }
  };

  const setBrandVariant = async (v) => {
    setVariantSaving(true);
    try {
      const company = await updateCompany({ brand_variant: v });
      setUser((u) => ({ ...u, company: { ...(u.company || {}), brand_variant: company.brand_variant } }));
      toast.success("Brand style updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setVariantSaving(false); }
  };

  const saveCompanyName = async () => {
    if (!companyName.trim()) { toast.error("Company name cannot be empty"); return; }
    setNameSaving(true);
    try {
      const c = await updateCompany({ name: companyName.trim() });
      setUser((u) => ({ ...u, company: { ...(u.company || {}), name: c.name } }));
      toast.success("Company name updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save");
    } finally { setNameSaving(false); }
  };

  if (user?.role !== "super_admin") return null;

  return (
    <SettingsSection title="Branding" description="Customise how your brand appears in the sidebar and on client invitations.">
      <div className="surface-card p-5 space-y-5" data-testid="company-branding-card">

        {/* Company name editor */}
        <div>
          <div className="text-xs font-medium text-slate-700 mb-1">Company name</div>
          <div className="flex items-center gap-2">
            <Input
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Your company name"
              className="max-w-xs"
              data-testid="company-name-input"
            />
            <Button type="button" size="sm" disabled={nameSaving} onClick={saveCompanyName} data-testid="company-name-save">
              {nameSaving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>

        {/* Variant selector */}
        <div>
          <div className="text-xs font-medium text-slate-700 mb-2">Sidebar brand style</div>
          <div className="flex flex-wrap gap-2" data-testid="brand-variant-selector">
            {[
              { value: "logo", label: "Logo only" },
              { value: "logomark_name", label: "Icon + name" },
              { value: "name", label: "Name only" },
            ].map(({ value, label }) => {
              const active = (user.company?.brand_variant || "logo") === value;
              return (
                <button
                  key={value}
                  type="button"
                  disabled={variantSaving}
                  onClick={() => setBrandVariant(value)}
                  data-testid={`brand-variant-${value}`}
                  className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
                    active
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Wide logo uploader */}
        <div>
          <div className="text-xs font-medium text-slate-700 mb-1">Wide logo</div>
          <div className="text-xs text-slate-500 mb-3">
            PNG/SVG, transparent background, about 240×80px, under 2 MB. Used for the "Logo only" style.
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-44 h-12 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden px-2">
              {user.company?.logo_url
                ? <img src={user.company.logo_url} alt="Company logo" className="max-w-full max-h-full object-contain" />
                : <Buildings size={24} className="text-slate-300" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-800 text-sm">{user.company?.name || "Your company"}</div>
              <div className="flex items-center gap-2 mt-2">
                <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden"
                       onChange={onLogoSelected} data-testid="logo-file-input" />
                <Button type="button" size="sm" variant="outline" disabled={uploading}
                        onClick={() => logoInputRef.current?.click()} data-testid="upload-logo-button">
                  {uploading ? "Uploading…" : (user.company?.logo_url ? "Replace logo" : "Upload logo")}
                </Button>
                {user.company?.logo_url && (
                  <Button type="button" size="sm" variant="ghost" disabled={uploading}
                          onClick={removeLogo} className="text-rose-600" data-testid="remove-logo-button">
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Square icon (logo-mark) uploader */}
        <div>
          <div className="text-xs font-medium text-slate-700 mb-1">Square icon (logo-mark)</div>
          <div className="text-xs text-slate-500 mb-3">
            Square icon (PNG/SVG), ~64×64, used for the "Icon + name" style.
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-12 h-12 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
              {user.company?.icon_url
                ? <img src={user.company.icon_url} alt="Company icon" className="max-w-full max-h-full object-contain" />
                : <Buildings size={20} className="text-slate-300" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mt-1">
                <input ref={iconInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden"
                       onChange={onIconSelected} data-testid="icon-file-input" />
                <Button type="button" size="sm" variant="outline" disabled={iconUploading}
                        onClick={() => iconInputRef.current?.click()} data-testid="upload-icon-button">
                  {iconUploading ? "Uploading…" : (user.company?.icon_url ? "Replace icon" : "Upload icon")}
                </Button>
                {user.company?.icon_url && (
                  <Button type="button" size="sm" variant="ghost" disabled={iconUploading}
                          onClick={removeIcon} className="text-rose-600" data-testid="remove-icon-button">
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>
    </SettingsSection>
  );
}
